import { useState } from "react";
import { loadGameScores, saveGameScores, type GameScores } from "../../lib/storage";
import { track } from "../../lib/track";
import SortGame from "../../components/minigames/SortGame";
import MemoryGame from "../../components/minigames/MemoryGame";
import MathGame from "../../components/minigames/MathGame";
import BudgetGame from "../../components/minigames/BudgetGame";
import TrueFalseGame from "../../components/minigames/TrueFalseGame";
import ScenarioGame from "../../components/minigames/ScenarioGame";
import CompoundGame from "../../components/minigames/CompoundGame";

type ScoreConfig =
  | { type: "high"; denom: number }   // higher number wins (TrueFalse, Pop)
  | { type: "low" };                   // lower number wins (Memory)

type GameDef = {
  id: string;
  label: string;
  desc: string;
  emoji: string;
  color: string;
  dark: string;
  component: any;
  scoreConfig?: ScoreConfig;
};

const CATEGORIES: { id: string; label: string; games: GameDef[] }[] = [
  {
    id: "brain",
    label: "🧠 Knowledge Check",
    games: [
      { id: "memory",    label: "Memory Match",   desc: "Match financial concepts — test what you know",  emoji: "🃏", color: "#8b5cf6", dark: "#5b21b6", component: MemoryGame,    scoreConfig: { type: "low" }              },
      { id: "truefalse", label: "True or False",  desc: "How well do you know your money facts?",         emoji: "⚡", color: "#0ea5e9", dark: "#0369a1", component: TrueFalseGame, scoreConfig: { type: "high", denom: 5 }   },
      { id: "compound",  label: "Compound Magic", desc: "Pick the best financial choices, earn stars",    emoji: "📈", color: "#10b981", dark: "#065f46", component: CompoundGame,  scoreConfig: { type: "high", denom: 3 }   },
    ],
  },
  {
    id: "math",
    label: "🔢 Math Practice",
    games: [
      { id: "math",   label: "Money Math",     desc: "Count and calculate with real money amounts", emoji: "🧮", color: "#10b981", dark: "#065f46", component: MathGame   },
      { id: "budget", label: "Budget Builder", desc: "Divide your money using the 50/30/20 rule",   emoji: "📊", color: "#f59e0b", dark: "#78350f", component: BudgetGame },
    ],
  },
  {
    id: "strategy",
    label: "🎯 Decision Making",
    games: [
      { id: "sort",     label: "Sort It Out",      desc: "Categorize needs vs wants, income vs expenses", emoji: "🗂️",  color: "#f97316", dark: "#9a3412", component: SortGame     },
      { id: "scenario", label: "Real-Life Choices", desc: "Make actual financial decisions under pressure", emoji: "🌟", color: "#ec4899", dark: "#9d174d", component: ScenarioGame },
    ],
  },
];

const ALL_GAMES = CATEGORIES.flatMap(c => c.games);

function bestLabel(id: string, cfg: ScoreConfig | undefined, scores: GameScores): string | null {
  const rec = scores[id];
  if (!rec) return null;
  if (cfg?.type === "high" && rec.best !== undefined) return `⭐ Best: ${rec.best}/${cfg.denom}`;
  if (cfg?.type === "low"  && rec.best !== undefined) return `🔄 Best: ${rec.best} moves`;
  if (rec.plays > 0) return `✅ ${rec.plays} attempt${rec.plays !== 1 ? "s" : ""}`;
  return null;
}

export default function GamesScreen({ tier = "builder" }: { tier?: "starter" | "builder" | "master" }) {
  const [playing, setPlaying]       = useState<string | null>(null);
  const [justFinished, setJustFinished] = useState<string | null>(null);
  const [lastScore, setLastScore]   = useState<number | undefined>(undefined);
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [gameScores, setGameScores] = useState<GameScores>(() => loadGameScores());

  const activeGame = playing ? ALL_GAMES.find(g => g.id === playing) : null;

  function handleComplete(score?: number) {
    const gameId = playing!;
    const game   = ALL_GAMES.find(g => g.id === gameId)!;
    track("minigame_played", { game: gameId });
    const prev   = gameScores[gameId] ?? { plays: 0 };
    const updated = { ...prev, plays: prev.plays + 1 };
    let newRec = false;

    if (score !== undefined && game.scoreConfig) {
      const cfg = game.scoreConfig;
      const isBetter = cfg.type === "high"
        ? (prev.best === undefined || score > prev.best)
        : (prev.best === undefined || score < prev.best);
      if (isBetter) {
        updated.best = score;
        newRec = prev.best !== undefined;
      }
    }

    const next = { ...gameScores, [gameId]: updated };
    setGameScores(next);
    saveGameScores(next);
    setLastScore(score);
    setIsNewRecord(newRec);
    setJustFinished(gameId);
    setPlaying(null);
  }

  function playAgain(id: string) {
    setJustFinished(null);
    setLastScore(undefined);
    setIsNewRecord(false);
    setPlaying(id);
  }

  /* ── PLAYING A GAME ── */
  if (activeGame) {
    const GameComp = activeGame.component as any;
    return (
      <div style={{ fontFamily: "Nunito", minHeight: "100vh", background: activeGame.color + "14", paddingBottom: 100 }}>
        <div style={{
          background: "#fff", borderBottom: `3px solid ${activeGame.color}55`,
          padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
          position: "sticky", top: 0, zIndex: 10, boxShadow: "0 2px 12px #0001",
        }}>
          <button
            onClick={() => setPlaying(null)}
            className="btn-bounce"
            style={{
              background: activeGame.color + "22", border: `2px solid ${activeGame.color}55`,
              borderRadius: 12, padding: "6px 12px", fontSize: 13, fontWeight: 800,
              color: activeGame.dark, cursor: "pointer",
            }}
          >
            ← Practice
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: activeGame.color, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1.5 }}>Practicing</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: activeGame.dark, fontFamily: "Fredoka One", lineHeight: 1.1 }}>
              {activeGame.emoji} {activeGame.label}
            </div>
          </div>
          {/* Best score pill in header */}
          {(() => {
            const badge = bestLabel(activeGame.id, activeGame.scoreConfig, gameScores);
            return badge ? (
              <div style={{
                background: activeGame.color + "18", border: `1.5px solid ${activeGame.color}44`,
                borderRadius: 10, padding: "4px 10px", fontSize: 11, fontWeight: 800, color: activeGame.dark,
                whiteSpace: "nowrap",
              }}>{badge}</div>
            ) : null;
          })()}
        </div>

        <div style={{ padding: "16px" }}>
          <div style={{
            background: "#fff", borderRadius: 22, padding: "18px 16px",
            border: `3px solid ${activeGame.color}30`, boxShadow: `0 8px 28px ${activeGame.color}22`,
          }}>
            <GameComp color={activeGame.color} dark={activeGame.dark} onComplete={handleComplete} tier={tier} />
          </div>
        </div>
      </div>
    );
  }

  /* ── FINISHED — score + play again ── */
  const finishedGame = justFinished ? ALL_GAMES.find(g => g.id === justFinished) : null;
  if (finishedGame) {
    const cfg = finishedGame.scoreConfig;
    const rec = gameScores[finishedGame.id];

    const scoreDisplay = lastScore !== undefined && cfg
      ? cfg.type === "high"
        ? `${lastScore} / ${cfg.denom} correct`
        : `${lastScore} moves`
      : null;

    const prevBest = rec?.best;
    const showRecord = isNewRecord && lastScore !== undefined;
    const showPersonalBest = !isNewRecord && lastScore !== undefined && cfg && prevBest !== undefined;

    return (
      <div style={{
        fontFamily: "Nunito", minHeight: "100vh",
        background: `linear-gradient(160deg, ${finishedGame.color}18, #fff 60%)`,
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "32px 20px", gap: 18, paddingBottom: 100,
      }}>
        <div style={{ fontSize: showRecord ? 80 : 72 }}>{showRecord ? "🏆" : "🎉"}</div>

        <div style={{ fontFamily: "Fredoka One", fontSize: 32, color: finishedGame.dark, textAlign: "center" }}>
          {showRecord ? "New Record!" : "Nice work!"}
        </div>

        <div style={{ fontSize: 15, color: "#6b7280", fontWeight: 700, textAlign: "center" }}>
          You finished {finishedGame.label}
        </div>

        {/* Score card */}
        {scoreDisplay && (
          <div style={{
            background: "#fff", borderRadius: 18, padding: "16px 28px", textAlign: "center",
            border: `2.5px solid ${finishedGame.color}44`,
            boxShadow: `0 6px 20px ${finishedGame.color}22`,
            minWidth: 200,
          }}>
            <div style={{ fontSize: 13, color: "#9ca3af", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>
              {cfg?.type === "low" ? "Completed in" : "Score"}
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: finishedGame.dark, fontFamily: "Fredoka One" }}>
              {scoreDisplay}
            </div>

            {showRecord && (
              <div style={{
                marginTop: 8, background: "#fef9c3", border: "1.5px solid #fbbf24",
                borderRadius: 8, padding: "4px 12px", fontSize: 13, fontWeight: 800, color: "#92400e",
              }}>
                🏆 New personal best!
              </div>
            )}

            {showPersonalBest && cfg?.type === "high" && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af", fontWeight: 700 }}>
                Personal best: {prevBest}/{cfg.denom}
              </div>
            )}
            {showPersonalBest && cfg?.type === "low" && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af", fontWeight: 700 }}>
                Personal best: {prevBest} moves
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => playAgain(finishedGame.id)}
          className="btn-bounce"
          style={{
            background: `linear-gradient(135deg, ${finishedGame.color}, ${finishedGame.dark})`,
            border: "none", borderRadius: 18, padding: "16px 32px",
            fontSize: 17, fontWeight: 900, color: "#fff", cursor: "pointer",
            fontFamily: "Fredoka One", boxShadow: `0 8px 24px ${finishedGame.color}55`,
          }}
        >
          Try Again 🔁
        </button>
        <button
          onClick={() => { setJustFinished(null); setLastScore(undefined); setIsNewRecord(false); }}
          className="btn-bounce"
          style={{
            background: "rgba(0,0,0,0.06)", border: "none", borderRadius: 14,
            padding: "12px 24px", fontSize: 14, fontWeight: 800, color: "#6b7280", cursor: "pointer",
          }}
        >
          Back to Practice
        </button>
      </div>
    );
  }

  /* ── GAMES LIST ── */
  return (
    <div style={{ fontFamily: "Nunito", paddingBottom: 100 }}>
      {/* Hero */}
      <div style={{
        background: "linear-gradient(135deg, #f97316 0%, #ec4899 50%, #8b5cf6 100%)",
        padding: "22px 18px 20px", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -20, right: -10, fontSize: 100, opacity: 0.1, transform: "rotate(10deg)" }}>🎯</div>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#fff9", letterSpacing: 3, textTransform: "uppercase", marginBottom: 2 }}>
          Money Skills
        </div>
        <div style={{ fontSize: 32, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", lineHeight: 1, marginBottom: 4 }}>
          Practice Zone 🎯
        </div>
        <div style={{ fontSize: 13, color: "#ffffffcc", fontWeight: 700 }}>
          {ALL_GAMES.length} exercises to build real money skills
        </div>
      </div>

      {/* Categories */}
      <div style={{ padding: "18px 16px 0", display: "flex", flexDirection: "column", gap: 24 }}>
        {CATEGORIES.map(cat => (
          <div key={cat.id}>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#374151", marginBottom: 10 }}>
              {cat.label}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {cat.games.map(game => {
                const badge = bestLabel(game.id, game.scoreConfig, gameScores);
                return (
                  <div
                    key={game.id}
                    style={{
                      background: "#fff",
                      border: `2.5px solid ${game.color}33`,
                      borderRadius: 18,
                      padding: "14px 16px",
                      display: "flex", alignItems: "center", gap: 14,
                      boxShadow: `0 4px 16px ${game.color}18`,
                    }}
                  >
                    <div style={{
                      width: 52, height: 52, borderRadius: 16, flexShrink: 0,
                      background: `linear-gradient(135deg, ${game.color}, ${game.dark})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 26,
                    }}>
                      {game.emoji}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 900, color: "#1f2937", marginBottom: 2 }}>
                        {game.label}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, lineHeight: 1.3 }}>
                        {game.desc}
                      </div>
                      {badge && (
                        <div style={{
                          display: "inline-block", marginTop: 5,
                          background: game.color + "18", border: `1.5px solid ${game.color}44`,
                          borderRadius: 8, padding: "2px 8px",
                          fontSize: 11, fontWeight: 800, color: game.dark,
                        }}>
                          {badge}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => { setJustFinished(null); setLastScore(undefined); setIsNewRecord(false); setPlaying(game.id); }}
                      className="btn-bounce"
                      style={{
                        background: `linear-gradient(135deg, ${game.color}, ${game.dark})`,
                        border: "none", borderRadius: 12, padding: "9px 16px",
                        fontSize: 13, fontWeight: 900, color: "#fff", cursor: "pointer",
                        whiteSpace: "nowrap", flexShrink: 0,
                        boxShadow: `0 4px 12px ${game.color}44`,
                      }}
                    >
                      Start →
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
